"use client";

import { useCallback, useEffect, useRef } from "react";
import useSWR from "swr";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { getValidSupabaseAccessToken } from "@/lib/supabase-access-token";
import { useRealtimeItems } from "@/lib/hooks/useRealtimeItems";
import {
  EMPTY_SELLER_MY_ITEMS_TAB_COUNTS,
  type SellerMyItemsTabCounts,
} from "@/lib/seller/sellerMyItemsTabAttention";

/** SWR cache key; revalidate from other screens after listing changes. */
export const SELLER_LISTING_ATTENTION_SWR_KEY = "/api/me/seller-attention";
const SELLER_ATTENTION_DEDUPING_MS = 30_000;
const SELLER_ATTENTION_REALTIME_MIN_REVALIDATE_MS = 15_000;

export type SellerAttentionPayload = {
  needsAttention: boolean;
  tabCounts: SellerMyItemsTabCounts;
  itemsNeedingAttentionCount: number;
  unreadListingNotificationsCount: number;
  /** Approved listings with a pending periodic availability confirmation. */
  availabilityCheckPendingCount: number;
  pendingAvailabilityItemIds: string[];
  /** Review-queue intent ping pending confirmations. */
  reviewIntentPendingCount: number;
  pendingReviewIntentItemIds: string[];
};

async function fetchSellerAttention(): Promise<SellerAttentionPayload> {
  const token = await getValidSupabaseAccessToken();
  if (!token) {
    return {
      needsAttention: false,
      tabCounts: { ...EMPTY_SELLER_MY_ITEMS_TAB_COUNTS },
      itemsNeedingAttentionCount: 0,
      unreadListingNotificationsCount: 0,
      availabilityCheckPendingCount: 0,
      pendingAvailabilityItemIds: [],
      reviewIntentPendingCount: 0,
      pendingReviewIntentItemIds: [],
    };
  }
  const res = await fetch(SELLER_LISTING_ATTENTION_SWR_KEY, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    return {
      needsAttention: false,
      tabCounts: { ...EMPTY_SELLER_MY_ITEMS_TAB_COUNTS },
      itemsNeedingAttentionCount: 0,
      unreadListingNotificationsCount: 0,
      availabilityCheckPendingCount: 0,
      pendingAvailabilityItemIds: [],
      reviewIntentPendingCount: 0,
      pendingReviewIntentItemIds: [],
    };
  }
  const payload = (await res.json()) as SellerAttentionPayload;
  return {
    ...payload,
    tabCounts: payload.tabCounts ?? { ...EMPTY_SELLER_MY_ITEMS_TAB_COUNTS },
  };
}

/**
 * Badge signal for My Items: state-based per-tab counts (nav dot + tab labels).
 */
export function useSellerListingAttention() {
  const { user, isAuthenticated } = useAuth();
  const pathname = usePathname();
  const onAdminRoute = (pathname ?? "").startsWith("/admin");
  const attentionActive = Boolean(isAuthenticated && user && !onAdminRoute);
  const lastRealtimeRevalidateAtRef = useRef(0);
  const realtimeRevalidateTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const { data, mutate } = useSWR(
    attentionActive ? SELLER_LISTING_ATTENTION_SWR_KEY : null,
    fetchSellerAttention,
    {
      dedupingInterval: SELLER_ATTENTION_DEDUPING_MS,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    },
  );

  const refresh = useCallback(
    () => mutate(undefined, { revalidate: true }),
    [mutate],
  );

  const queueRealtimeRevalidate = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastRealtimeRevalidateAtRef.current;

    if (elapsed >= SELLER_ATTENTION_REALTIME_MIN_REVALIDATE_MS) {
      lastRealtimeRevalidateAtRef.current = now;
      void refresh();
      return;
    }

    if (realtimeRevalidateTimerRef.current) {
      return;
    }

    const waitMs = SELLER_ATTENTION_REALTIME_MIN_REVALIDATE_MS - elapsed;
    realtimeRevalidateTimerRef.current = setTimeout(() => {
      realtimeRevalidateTimerRef.current = null;
      lastRealtimeRevalidateAtRef.current = Date.now();
      void refresh();
    }, waitMs);
  }, [refresh]);

  useEffect(() => {
    if (attentionActive) return;
    if (!realtimeRevalidateTimerRef.current) return;
    clearTimeout(realtimeRevalidateTimerRef.current);
    realtimeRevalidateTimerRef.current = null;
  }, [attentionActive]);

  useEffect(() => {
    return () => {
      if (!realtimeRevalidateTimerRef.current) return;
      clearTimeout(realtimeRevalidateTimerRef.current);
      realtimeRevalidateTimerRef.current = null;
    };
  }, []);

  useRealtimeItems({
    table: "items",
    filter: user ? `seller_id=eq.${user.id}` : undefined,
    enabled: attentionActive,
    onInsert: queueRealtimeRevalidate,
    onUpdate: queueRealtimeRevalidate,
    onDelete: queueRealtimeRevalidate,
    channelName: "seller-attention-items",
  });
  // Keep this hook lightweight: item-status changes are the primary live signal.
  // We coalesce realtime bursts to reduce repeated API work.

  return {
    needsAttention: data?.needsAttention ?? false,
    tabCounts: data?.tabCounts ?? EMPTY_SELLER_MY_ITEMS_TAB_COUNTS,
    itemsNeedingAttentionCount: data?.itemsNeedingAttentionCount ?? 0,
    unreadListingNotificationsCount: data?.unreadListingNotificationsCount ?? 0,
    availabilityCheckPendingCount: data?.availabilityCheckPendingCount ?? 0,
    pendingAvailabilityItemIds: data?.pendingAvailabilityItemIds ?? [],
    reviewIntentPendingCount: data?.reviewIntentPendingCount ?? 0,
    pendingReviewIntentItemIds: data?.pendingReviewIntentItemIds ?? [],
    refresh,
  };
}
